import client from './client';
import { fetchAllPages } from './fetchAllPages';
import { createOfflineUuid, enqueuePendingWrite, isOffline } from './offlineMutationQueue';
import { applyApprovedOnlyParams, applyScopedParams, getCurrentRoleId, getCurrentUserScope } from './viewerAccess';
import { publishCollectionChange } from '../utils/collectionSync';
import { extractLocationFields } from '../utils/scopeFields';
import { getSharedRecords, mergeRecordsByUuid, removeSharedRecord, upsertSharedRecord, upsertSharedRecords } from './sharedRecordCache';

const COLLECTION_KEY = 'population-records';

function humanize(str) {
  return str ? str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : null;
}

function normalizeRecord(row = {}) {
  const location = extractLocationFields(row);
  // Species: backend may return species_code flat, a nested species object, or just species_uuid
  const speciesObj = row.species && typeof row.species === 'object' ? row.species : null;
  const speciesDisplay =
    row.species_code ??
    speciesObj?.common_name_nigeria ??
    speciesObj?.species_code ??
    (typeof row.species === 'string' && row.species ? row.species : null) ??
    null;
  const speciesUuid = row.species_uuid ?? speciesObj?.uuid ?? null;

  // Breed: same pattern
  const breedObj = row.breed && typeof row.breed === 'object' ? row.breed : null;
  const breedDisplay =
    row.breed_name ??
    row.breed_code ??
    breedObj?.breed_name ??
    breedObj?.breed_code ??
    (typeof row.breed === 'string' && row.breed ? row.breed : null) ??
    null;
  const breedUuid = row.breed_uuid ?? breedObj?.uuid ?? null;

  // production_purpose may be a code string ("dairy") or a nested object
  const purposeRaw = row.production_purpose;
  const productionPurpose = purposeRaw && typeof purposeRaw === 'object'
    ? (purposeRaw.purpose_label ?? purposeRaw.label ?? purposeRaw.name ?? humanize(purposeRaw.purpose_code ?? purposeRaw.code ?? purposeRaw.value ?? ''))
    : (row.production_purpose_label ?? row.productionPurposeLabel ?? humanize(typeof purposeRaw === 'string' ? purposeRaw : null));
  const productionPurposeUuid =
    row.production_purpose_uuid ??
    purposeRaw?.uuid ??
    purposeRaw?.id ??
    null;
  const productionPurposeCode =
    typeof purposeRaw === 'string'
      ? purposeRaw
      : (purposeRaw?.purpose_code ?? purposeRaw?.code ?? null);
  const herdsFlocksCount =
    row.herds_flocks_count ??
    row.number_herds_flocks ??
    row.herds_flocks ??
    row.no_herds_flocks ??
    null;

  return {
    uuid:               row.uuid,
    id:                 row.uuid,
    state:              location.state         ?? null,
    lga:                location.lga           ?? null,
    ward:               location.ward          ?? null,
    period:             row.reference_period    ?? null,
    species:            speciesDisplay,
    speciesUuid,
    breedEcotype:       breedDisplay,
    breedUuid,
    productionSystem:   humanize(row.production_system),
    productionPurpose,
    productionPurposeLabel: productionPurpose,
    productionPurposeUuid,
    productionPurposeCode,
    populationCount:    row.population_count    ?? null,
    sex:                humanize(row.sex),
    ageClass:           row.age_class_code      ?? null,
    ageClassCode:       row.age_class_code      ?? null,
    numberHerdsFlocks:  herdsFlocksCount,
    herdsFlocksCount,
    periodOfftake:      row.period_offtake      ?? null,
    countMethod:        humanize(row.count_method),
    countMethodCode:    row.count_method        ?? null,
    dataSource:         row.data_source         ?? null,
    confidenceLevel:    humanize(row.confidence_level),
    notes:              row.notes               ?? null,
    validationStatus:   humanize(row.data_status) ?? 'Draft',
    data_status:        row.data_status         ?? 'draft',
    created_at:         row.created_at          ?? null,
    created_by_uuid:    row.created_by?.uuid ?? row.created_by_uuid ?? null,
    updated_at:         row.updated_at          ?? null,
    submitted_by:       row.submitted_by        ?? null,
    submitted_at:       row.submitted_at        ?? null,
    validated_by:       row.validated_by        ?? null,
    validated_at:       row.validated_at        ?? null,
    approved_by:        row.approved_by         ?? null,
    approved_at:        row.approved_at         ?? null,
    rejected_at:        row.rejected_at         ?? null,
    rejection_notes:    row.rejection_notes     ?? null,
  };
}

function normalizeWritePayload(payload = {}) {
  const productionPurposeUuid =
    payload.production_purpose_uuid ??
    payload.productionPurposeUuid ??
    payload.production_purpose ??
    payload.productionPurposeCode ??
    payload.productionPurpose ??
    null;
  const herdsFlocksCount =
    payload.herds_flocks_count ??
    payload.herdsFlocksCount ??
    payload.number_herds_flocks ??
    payload.numberHerdsFlocks ??
    null;

  return {
    ...payload,
    production_purpose_uuid: productionPurposeUuid || null,
    herds_flocks_count: herdsFlocksCount !== '' && herdsFlocksCount != null
      ? Number(herdsFlocksCount)
      : null,
  };
}

function extractOne(data) {
  const d = data?.data ?? {};
  if (Array.isArray(d.items)) return normalizeRecord(d.items[0] ?? {});
  return normalizeRecord(d);
}

function unwrapPayload(responseData) {
  return responseData?.data ?? responseData ?? {};
}

function extractCollection(responseData) {
  const payload = unwrapPayload(responseData);
  const envelope =
    (Array.isArray(payload?.items) || Array.isArray(payload?.results) || payload?.pagination)
      ? payload
      : payload?.data ?? payload;

  const items = Array.isArray(envelope?.items)
    ? envelope.items
    : Array.isArray(envelope?.results)
      ? envelope.results
      : Array.isArray(envelope)
        ? envelope
        : Array.isArray(payload?.items)
          ? payload.items
          : Array.isArray(payload?.results)
            ? payload.results
            : Array.isArray(payload)
              ? payload
              : [];

  return {
    items: items.map(normalizeRecord),
    pagination: envelope?.pagination ?? payload?.pagination ?? null,
  };
}

function findCachedRecord(uuid) {
  const key = String(uuid ?? '').trim();
  if (!key) return null;

  return getSharedRecords(COLLECTION_KEY).find((record) => String(record?.uuid ?? record?.id ?? '').trim() === key) ?? null;
}

function buildOptimisticRecord(payload = {}, uuid, existing = null) {
  const now = new Date().toISOString();
  return normalizeRecord({
    ...(existing ?? {}),
    ...payload,
    uuid,
    id: uuid,
    created_at: existing?.created_at ?? payload.created_at ?? now,
    updated_at: now,
    data_status: payload.data_status ?? existing?.data_status ?? 'draft',
  });
}

function applyOptimisticStatus(record = {}, action, notes = '') {
  const next = { ...record };
  const statusMap = {
    submit: 'submitted',
    validate: 'validated',
    approve: 'approved',
    reject: 'rejected',
    draft: 'draft',
    revise: 'draft',
    recall: 'draft',
    reset: 'draft',
    revert: 'draft',
  };
  const status = statusMap[action];

  if (status) {
    next.data_status = status;
    next.validationStatus = humanize(status);
  }

  if (action === 'reject') {
    const rejectionNotes = String(notes ?? '').trim();
    if (rejectionNotes) {
      next.rejection_notes = rejectionNotes;
    }
  }

  return normalizeRecord(next);
}

function queueOfflinePopulationWrite({ action, method = 'post', url, data = null, tempUuid = null, recordUuid = null }) {
  enqueuePendingWrite({
    collectionKey: COLLECTION_KEY,
    action,
    method,
    url,
    data,
    tempUuid,
    recordUuid,
  });
}

// The shared-record cache (sharedRecordCache.js) persists in localStorage keyed
// only by collection name, not by user/role/jurisdiction — so a record fetched by
// one role (e.g. R01 browsing nationally) can otherwise leak into a later R04/R05
// session's merged results even though the live API call was correctly scoped.
// Re-filter the merged set by the current viewer's own state/lga before returning.
function filterByScope(items = []) {
  const roleId = getCurrentRoleId();
  if (roleId !== 'R04' && roleId !== 'R05') return items;

  const scope = getCurrentUserScope(roleId);
  const scopedState = String(scope.state ?? '').trim().toLowerCase();
  const scopedLga = String(scope.lga ?? '').trim().toLowerCase();

  return items.filter((item) => {
    if (scopedState && String(item?.state ?? '').trim().toLowerCase() !== scopedState) return false;
    if (roleId === 'R05' && scopedLga && String(item?.lga ?? '').trim().toLowerCase() !== scopedLga) return false;
    return true;
  });
}

async function fetchPage(params = {}) {
  const requestParams = applyScopedParams(applyApprovedOnlyParams({
    per_page: params.per_page ?? 200,
    ...params,
    page: params.page ?? 1,
  }));

  try {
    const { data } = await client.get('/v1/population-records', { params: requestParams });
    const live = extractCollection(data);
    if (Array.isArray(live.items) && live.items.length > 0) {
      upsertSharedRecords(COLLECTION_KEY, live.items);
    }
    return {
      ...live,
      items: filterByScope(mergeRecordsByUuid(live.items ?? [], getSharedRecords(COLLECTION_KEY))),
    };
  } catch (error) {
    if (isOffline()) {
      const cached = getSharedRecords(COLLECTION_KEY);
      return {
        items: filterByScope(cached.map(normalizeRecord)),
        pagination: null,
      };
    }
    throw error;
  }
}

export const populationRecordsApi = {
  collectionKey: COLLECTION_KEY,

  async list(params = {}) {
    return fetchPage(params);
  },

  async listAll(params = {}) {
    return fetchAllPages((page) => fetchPage({ ...params, page }), { pageSize: params.per_page ?? 200 });
  },

  async get(uuid) {
    try {
      const { data } = await client.get(`/v1/population-records/${uuid}`);
      const item = extractOne(data);
      if (item?.uuid) upsertSharedRecord(COLLECTION_KEY, item);
      return item;
    } catch (error) {
      if (isOffline()) {
        const cached = findCachedRecord(uuid);
        if (cached) return normalizeRecord(cached);
        return normalizeRecord({ uuid, id: uuid });
      }
      throw error;
    }
  },

  async create(payload) {
    const requestPayload = normalizeWritePayload(payload);
    if (isOffline()) {
      const tempUuid = createOfflineUuid(COLLECTION_KEY);
      const optimistic = buildOptimisticRecord(requestPayload, tempUuid);
      upsertSharedRecord(COLLECTION_KEY, optimistic);
      queueOfflinePopulationWrite({
        action: 'create',
        method: 'post',
        url: '/v1/population-records',
        data: requestPayload,
        tempUuid,
        recordUuid: tempUuid,
      });
      publishCollectionChange(COLLECTION_KEY, { action: 'create', uuid: tempUuid, offline: true });
      return optimistic;
    }

    const { data } = await client.post('/v1/population-records', requestPayload);
    const item = extractOne(data);
    if (item?.uuid) upsertSharedRecord(COLLECTION_KEY, item);
    publishCollectionChange(COLLECTION_KEY, { action: 'create', uuid: item?.uuid ?? null });
    return item;
  },

  async update(uuid, payload) {
    const requestPayload = normalizeWritePayload(payload);
    if (isOffline()) {
      const existing = findCachedRecord(uuid);
      const optimistic = buildOptimisticRecord(requestPayload, uuid, existing);
      upsertSharedRecord(COLLECTION_KEY, optimistic);
      queueOfflinePopulationWrite({
        action: 'update',
        method: 'put',
        url: `/v1/population-records/${uuid}`,
        data: requestPayload,
        recordUuid: uuid,
      });
      publishCollectionChange(COLLECTION_KEY, { action: 'update', uuid, offline: true });
      return optimistic;
    }

    const { data } = await client.put(`/v1/population-records/${uuid}`, requestPayload);
    const item = extractOne(data);
    if (item?.uuid) upsertSharedRecord(COLLECTION_KEY, item);
    publishCollectionChange(COLLECTION_KEY, { action: 'update', uuid });
    return item;
  },

  async remove(uuid) {
    if (isOffline()) {
      removeSharedRecord(COLLECTION_KEY, uuid);
      queueOfflinePopulationWrite({
        action: 'remove',
        method: 'delete',
        url: `/v1/population-records/${uuid}`,
        recordUuid: uuid,
      });
      publishCollectionChange(COLLECTION_KEY, { action: 'remove', uuid, offline: true });
      return { uuid, removed: true, offline: true };
    }

    await client.delete(`/v1/population-records/${uuid}`);
    removeSharedRecord(COLLECTION_KEY, uuid);
    publishCollectionChange(COLLECTION_KEY, { action: 'remove', uuid });
  },

  async submit(uuid) {
    if (isOffline()) {
      const optimistic = applyOptimisticStatus(findCachedRecord(uuid) ?? { uuid, id: uuid }, 'submit');
      upsertSharedRecord(COLLECTION_KEY, optimistic);
      queueOfflinePopulationWrite({
        action: 'submit',
        method: 'post',
        url: `/v1/population-records/${uuid}/submit`,
        recordUuid: uuid,
      });
      publishCollectionChange(COLLECTION_KEY, { action: 'submit', uuid, offline: true });
      return optimistic;
    }

    const { data } = await client.post(`/v1/population-records/${uuid}/submit`);
    const item = extractOne(data);
    if (item?.uuid) upsertSharedRecord(COLLECTION_KEY, item);
    publishCollectionChange(COLLECTION_KEY, { action: 'submit', uuid });
    return item;
  },

  async validate(uuid) {
    if (isOffline()) {
      const optimistic = applyOptimisticStatus(findCachedRecord(uuid) ?? { uuid, id: uuid }, 'validate');
      upsertSharedRecord(COLLECTION_KEY, optimistic);
      queueOfflinePopulationWrite({
        action: 'validate',
        method: 'post',
        url: `/v1/population-records/${uuid}/validate`,
        recordUuid: uuid,
      });
      publishCollectionChange(COLLECTION_KEY, { action: 'validate', uuid, offline: true });
      return optimistic;
    }

    const { data } = await client.post(`/v1/population-records/${uuid}/validate`);
    const item = extractOne(data);
    if (item?.uuid) upsertSharedRecord(COLLECTION_KEY, item);
    publishCollectionChange(COLLECTION_KEY, { action: 'validate', uuid });
    return item;
  },

  async approve(uuid) {
    if (isOffline()) {
      const optimistic = applyOptimisticStatus(findCachedRecord(uuid) ?? { uuid, id: uuid }, 'approve');
      upsertSharedRecord(COLLECTION_KEY, optimistic);
      queueOfflinePopulationWrite({
        action: 'approve',
        method: 'post',
        url: `/v1/population-records/${uuid}/approve`,
        recordUuid: uuid,
      });
      publishCollectionChange(COLLECTION_KEY, { action: 'approve', uuid, offline: true });
      return optimistic;
    }

    const { data } = await client.post(`/v1/population-records/${uuid}/approve`);
    const item = extractOne(data);
    if (item?.uuid) upsertSharedRecord(COLLECTION_KEY, item);
    publishCollectionChange(COLLECTION_KEY, { action: 'approve', uuid });
    return item;
  },
  async resetToDraft(uuid) {
    if (isOffline()) {
      const optimistic = applyOptimisticStatus(findCachedRecord(uuid) ?? { uuid, id: uuid }, 'reset');
      upsertSharedRecord(COLLECTION_KEY, optimistic);
      queueOfflinePopulationWrite({
        action: 'reset',
        method: 'post',
        url: `/v1/population-records/${uuid}/draft`,
        recordUuid: uuid,
      });
      publishCollectionChange(COLLECTION_KEY, { action: 'reset', uuid, offline: true });
      return optimistic;
    }

    // Try the most common backend action names for "reset to draft" in order
    for (const action of ['draft', 'revise', 'recall', 'reset', 'revert']) {
      try {
        await client.post(`/v1/population-records/${uuid}/${action}`);
        publishCollectionChange(COLLECTION_KEY, { action, uuid });
        return;
      } catch (e) {
        if (e?.response?.status !== 404) throw e; // real error — stop
        // 404 = that endpoint name doesn't exist, try the next
      }
    }
    throw new Error('Your server does not support resetting a record to Draft. Please contact the backend team to add a /draft or /revise endpoint.');
  },
  async reject(uuid, notes = '') {
    if (!notes || !notes.trim()) throw new Error('Rejection reason is required.');
    if (isOffline()) {
      const optimistic = applyOptimisticStatus(findCachedRecord(uuid) ?? { uuid, id: uuid }, 'reject', notes.trim());
      upsertSharedRecord(COLLECTION_KEY, optimistic);
      queueOfflinePopulationWrite({
        action: 'reject',
        method: 'post',
        url: `/v1/population-records/${uuid}/reject`,
        data: { notes: notes.trim() },
        recordUuid: uuid,
      });
      publishCollectionChange(COLLECTION_KEY, { action: 'reject', uuid, offline: true });
      return optimistic;
    }
    await client.post(`/v1/population-records/${uuid}/reject`, { notes: notes.trim() });
    publishCollectionChange(COLLECTION_KEY, { action: 'reject', uuid });
  },

  // Returns total livestock headcount — SUM of population_count across all records.
  // Matches exactly what SummaryCards.jsx (Livestock module) displays as
  // "Total National Livestock Population".
  async totalHeadcount() {
    // 1. Dedicated summary endpoint (when backend adds it)
    try {
      const { data } = await client.get('/v1/population-records/summary', {
        params: applyScopedParams(applyApprovedOnlyParams({})),
      });
      const payload = data?.data ?? data ?? {};
      const n = Number(
        payload.total_population_count ??
        payload.total_population        ??
        payload.population_total        ??
        payload.total
      );
      if (Number.isFinite(n) && n > 0) return n;
    } catch { /* endpoint does not exist yet */ }

    // 2. Aggregates block that some backends embed in the list envelope
    try {
      const { data } = await client.get('/v1/population-records', {
        params: applyScopedParams(applyApprovedOnlyParams({ per_page: 1 })),
      });
      const payload = data?.data ?? data ?? {};
      const totals  = payload.totals ?? payload.aggregates ?? {};
      const n = Number(
        totals.population_count ??
        totals.total_population ??
        totals.total
      );
      if (Number.isFinite(n) && n > 0) return n;
    } catch { /* ignore */ }

    // 3. Fetch all records and sum population_count — same logic as SummaryCards.jsx
    try {
      const { items } = await populationRecordsApi.listAll({ per_page: 1000 });
      const total = items.reduce(
        (sum, row) => sum + (Number(row.populationCount ?? row.population_count) || 0), 0
      );
      if (total > 0) return total;
    } catch { /* ignore */ }

    return 0;
  },
};
